/**
 * OrdersCardsView — Trioty Orders card grid. A responsive auto-fill grid
 * (min 300px columns, 14px gap) of `OrderCard`s, grouped into action-first
 * reason sections (headers hidden when there's a single section). See
 * OrdersGridCard for the card anatomy.
 */

import { useState } from "react";
import type { OrderStatus, SellerOrderListItem } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { useThemeStore } from "@/shared/theme/useTheme";
import { OrderCard } from "./OrdersGridCard";
import { OrderHoverTooltip, type HoverTip } from "./order-row/OrderHoverTooltip";
import { fulVisual } from "./order-row/order-visuals";
import { groupOrdersForList } from "@/modules/orders/lib/orders-attention-model";

// ─── Props ────────────────────────────────────────────────────────────────────

type Props = {
  orders: SellerOrderListItem[];
  onSelectOrder: (id: string) => void;
  onQuickAction: (order: SellerOrderListItem, targetStatus: OrderStatus) => void;
  onOpenQuickSheet: (order: SellerOrderListItem) => void;
  quickActionBusyId: string | null;
};

const GRID = "grid gap-3.5 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]";

// ─── Grid wrapper ─────────────────────────────────────────────────────────────

export function OrdersCardsView({
  orders,
  onSelectOrder,
  onQuickAction,
  onOpenQuickSheet,
  quickActionBusyId,
}: Props) {
  const t = useTranslations();
  const isDark = useThemeStore((s) => s.resolvedDark);
  const [tip, setTip] = useState<HoverTip | null>(null);

  const sections = groupOrdersForList(orders);
  const showHeaders = sections.length > 1;

  const renderCard = (o: SellerOrderListItem) => (
    <OrderCard
      key={o.id}
      order={o}
      onSelect={() => onSelectOrder(o.id)}
      onQuickAction={onQuickAction}
      onOpenQuickSheet={onOpenQuickSheet}
      busy={quickActionBusyId === o.id}
      onHoverStart={(e) => setTip({ order: o, x: e.clientX, y: e.clientY })}
      onHoverMove={(e) =>
        setTip((prev) => (prev ? { order: o, x: e.clientX, y: e.clientY } : prev))
      }
      onHoverEnd={() => setTip((prev) => (prev?.order.id === o.id ? null : prev))}
    />
  );

  return (
    <>
      {showHeaders ? (
        <div className="flex flex-col gap-5">
          {sections.map((section) => (
            <div key={section.key}>
              <div className="mb-2.5 flex items-center gap-2">
                <span
                  className="h-[7px] w-[7px] shrink-0 rounded-full"
                  style={{
                    background: section.sampleStatus
                      ? fulVisual(section.sampleStatus, isDark).color
                      : "var(--text-muted)",
                  }}
                />
                <span className="text-[11px] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-secondary)]">
                  {t(section.labelKey)}
                </span>
                <span className="text-[11px] font-bold text-[color:var(--text-disabled)]">
                  {section.orders.length}
                </span>
              </div>
              <div className={GRID}>{section.orders.map(renderCard)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className={GRID}>{orders.map(renderCard)}</div>
      )}
      <OrderHoverTooltip tip={tip} />
    </>
  );
}
